import { useState } from "react";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useMyPortalClients } from "@/hooks/usePortalClientAccess";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Wine,
  GlassWater,
  Package,
  CalendarDays,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";

// ─── Data Hooks (client-scoped via RLS) ─────────────────────────────────

function useClientInfo() {
  const { data: profile } = useUserProfile();
  return useQuery({
    queryKey: ["portal-client-info", (profile as any)?.client_id],
    enabled: !!(profile as any)?.client_id,
    queryFn: async () => {
      const { data } = await supabase
        .from("client")
        .select("name, code, contact_email, phone")
        .eq("id", (profile as any)?.client_id)
        .single();
      return data ?? null;
    },
  });
}

// Helper: ids of every client (brand) this login can see. Sorted so the
// query keys stay stable.
function useClientIds() {
  const { data: clients } = useMyPortalClients();
  return clients?.map((c) => c.id).sort();
}

// bottling_event.client_id references user_account(id) — the submitting
// login — not client(id).
function useUserAccountId() {
  const { data: profile } = useUserProfile();
  return profile?.id;
}

function usePortalStats() {
  const clientIds = useClientIds();
  const userAccountId = useUserAccountId();
  return useQuery({
    queryKey: ["portal-stats", clientIds, userAccountId],
    enabled: !!clientIds && !!userAccountId,
    queryFn: async () => {
      const [lots, bottling] = await Promise.all([
        supabase.from("wine_lot").select("id, current_volume_l").in("client_id", clientIds!),
        supabase
          .from("bottling_event")
          .select("id, status")
          .eq("client_id", userAccountId!)
          .in("status", ["submitted", "confirmed", "locked"]),
      ]);

      const totalVolume = (lots.data ?? []).reduce(
        (sum, l) => sum + (Number(l.current_volume_l) || 0),
        0,
      );

      return {
        totalLots: lots.data?.length ?? 0,
        totalVolume: Math.round(totalVolume),
        upcomingBottling: bottling.data?.length ?? 0,
      };
    },
  });
}

function usePortalLots() {
  const clientIds = useClientIds();
  return useQuery({
    queryKey: ["portal-lots", clientIds],
    enabled: !!clientIds,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("wine_lot")
        .select("id, code, varietal, vintage, current_volume_l, updated_at")
        .in("client_id", clientIds!)
        .order("code");

      if (error) throw error;
      return data ?? [];
    },
  });
}

function usePortalBottling() {
  const userAccountId = useUserAccountId();
  return useQuery({
    queryKey: ["portal-bottling", userAccountId],
    enabled: !!userAccountId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bottling_event")
        .select(
          "id, name, status, est_cases, confirmed_date, preferred_date_from, preferred_date_to, client_notes, facility",
        )
        .eq("client_id", userAccountId!)
        .order("confirmed_date", { ascending: true, nullsFirst: false });

      if (error) throw error;
      return data ?? [];
    },
  });
}

// ─── Status Helpers ─────────────────────────────────────────────────────

const bottlingStatusMap: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  draft: { label: "Draft", variant: "secondary" },
  submitted: { label: "Submitted", variant: "outline" },
  confirmed: { label: "Confirmed", variant: "default" },
  locked: { label: "Locked", variant: "default" },
  completed: { label: "Completed", variant: "secondary" },
};

function litersToGallons(liters: number) {
  return Math.round(liters * 0.264172);
}

// ─── Page ───────────────────────────────────────────────────────────────

export default function CustomerPortal() {
  const { data: clientInfo } = useClientInfo();
  const { data: myClients = [] } = useMyPortalClients();
  const heading =
    myClients.length > 1 ? myClients.map((c) => c.name).join(" · ") : clientInfo?.name ?? "Welcome";
  const { data: stats, isLoading: statsLoading } = usePortalStats();
  // Prefetch all tab data in parallel on page load
  const lots = usePortalLots();
  const bottling = usePortalBottling();

  return (
    <div className="flex-1 space-y-8 p-6 lg:p-8 max-w-6xl mx-auto">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-balance">
          {heading}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Your wine operations at a glance.
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-3">
        {statsLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))
        ) : (
          <>
            <StatTile
              label="Wine Lots"
              value={stats?.totalLots ?? 0}
              sub={`${litersToGallons(stats?.totalVolume ?? 0).toLocaleString()} gal total`}
              icon={Wine}
            />
            <StatTile
              label="Upcoming Bottling"
              value={stats?.upcomingBottling ?? 0}
              sub="Scheduled events"
              icon={GlassWater}
            />
            <StatTile
              label="Total Volume"
              value={`${litersToGallons(stats?.totalVolume ?? 0).toLocaleString()}`}
              sub="Gallons across all lots"
              icon={Package}
            />
          </>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="lots" className="space-y-6">
        <TabsList>
          <TabsTrigger value="lots" className="gap-1.5">
            <Wine className="h-3.5 w-3.5" />
            Wine Lots
          </TabsTrigger>
          <TabsTrigger value="bottling" className="gap-1.5">
            <GlassWater className="h-3.5 w-3.5" />
            Bottling
          </TabsTrigger>
        </TabsList>

        <div className="border rounded-lg bg-card min-h-[320px]">
          <TabsContent value="lots" className="mt-0">
            <LotsPanel data={lots.data} isLoading={lots.isLoading} />
          </TabsContent>
          <TabsContent value="bottling" className="mt-0">
            <BottlingPanel data={bottling.data} isLoading={bottling.isLoading} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// ─── Stat Tile ──────────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  sub,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  sub: string;
  icon: React.ElementType;
}) {
  return (
    <Card className="transition-all hover:shadow-md">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium truncate">{label}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{sub}</p>
      </CardContent>
    </Card>
  );
}

// ─── Wine Lots Panel ────────────────────────────────────────────────────

function LotsPanel({ data: lots = [], isLoading }: { data?: any[]; isLoading: boolean }) {
  if (isLoading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-6 w-6 border-2 border-muted-foreground/20 border-t-muted-foreground" /></div>;

  if (lots.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No wine lots on file.
      </div>
    );
  }

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-nowrap">Lot Code</TableHead>
            <TableHead className="whitespace-nowrap">Varietal</TableHead>
            <TableHead className="whitespace-nowrap">Vintage</TableHead>
            <TableHead className="whitespace-nowrap text-right">Volume (gal)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {lots.map((lot: any) => (
            <TableRow key={lot.id}>
              <TableCell className="font-medium">{lot.code}</TableCell>
              <TableCell>{lot.varietal || "—"}</TableCell>
              <TableCell className="tabular-nums">{lot.vintage || "—"}</TableCell>
              <TableCell className="text-right tabular-nums">
                {lot.current_volume_l
                  ? litersToGallons(Number(lot.current_volume_l)).toLocaleString()
                  : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ─── Bottling Panel ─────────────────────────────────────────────────────

function BottlingPanel({ data: events = [], isLoading }: { data?: any[]; isLoading: boolean }) {
  if (isLoading) return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-6 w-6 border-2 border-muted-foreground/20 border-t-muted-foreground" /></div>;

  if (events.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No bottling events scheduled.
      </div>
    );
  }

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="whitespace-nowrap">Event</TableHead>
            <TableHead className="whitespace-nowrap">Status</TableHead>
            <TableHead className="whitespace-nowrap">Date</TableHead>
            <TableHead className="whitespace-nowrap">Facility</TableHead>
            <TableHead className="whitespace-nowrap text-right">Est. Cases</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((ev: any) => {
            const cfg = bottlingStatusMap[ev.status] ?? {
              label: ev.status,
              variant: "secondary" as const,
            };
            const dateDisplay = ev.confirmed_date
              ? format(new Date(ev.confirmed_date), "MMM d, yyyy")
              : ev.preferred_date_from
                ? `${format(new Date(ev.preferred_date_from), "MMM d")}–${ev.preferred_date_to ? format(new Date(ev.preferred_date_to), "MMM d") : "TBD"}`
                : "TBD";

            return (
              <TableRow key={ev.id}>
                <TableCell className="font-medium">
                  {ev.name || "Bottling Run"}
                </TableCell>
                <TableCell>
                  <Badge variant={cfg.variant}>{cfg.label}</Badge>
                </TableCell>
                <TableCell className="text-sm whitespace-nowrap">
                  <span className="flex items-center gap-1.5">
                    <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                    {dateDisplay}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {ev.facility || "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {ev.est_cases?.toLocaleString() || "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
